import React, { useEffect, useRef, useState } from 'react';
import { downloadFile } from '../lib/download';
import { escapeHtml, fetchBlob, printHtml, viewerKind } from '../lib/media';

// Bundled pdf.js worker (resolved by webpack at build time).
const PDF_WORKER_SRC = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

const noContextMenu = (e) => e.preventDefault();

// Full-screen in-app viewer for a chat attachment, with Print. When `locked`
// (a "View & Print only" file from someone else) there is no Download, the
// right-click menu and drag-to-save are disabled, and PDFs are drawn onto
// canvases with pdf.js instead of the browser's PDF viewer (whose toolbar has
// its own download button).
export default function MediaViewer({ message, locked, onClose }) {
  const { fileUrl, fileName } = message;
  const kind = viewerKind(message);
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(kind === 'pdf');
  const pagesRef = useRef(null);

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Load text / PDF content into memory.
  useEffect(() => {
    let cancelled = false;
    if (kind === 'text') {
      fetchBlob(fileUrl)
        .then((b) => b.text())
        .then((t) => !cancelled && setText(t))
        .catch(() => !cancelled && setError('Could not open this file.'));
    }
    if (kind === 'pdf') {
      (async () => {
        try {
          const pdfjsLib = await import('pdfjs-dist/build/pdf');
          pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
          const data = await (await fetchBlob(fileUrl)).arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data }).promise;
          const container = pagesRef.current;
          if (cancelled || !container) return;
          container.innerHTML = '';
          for (let n = 1; n <= pdf.numPages; n += 1) {
            const page = await pdf.getPage(n);
            const viewport = page.getViewport({ scale: 1.6 });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.className = 'block mx-auto mb-3 shadow bg-white max-w-full h-auto';
            canvas.addEventListener('contextmenu', noContextMenu);
            if (cancelled) return;
            container.appendChild(canvas);
            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          }
        } catch (e) {
          console.error('PDF render failed:', e);
          if (!cancelled) setError('Could not open this PDF. The link may have expired — reopen the chat.');
        } finally {
          if (!cancelled) setPdfLoading(false);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [fileUrl, kind]);

  const print = () => {
    const title = fileName || 'Print';
    if (kind === 'image') {
      printHtml(`<img src="${escapeHtml(fileUrl)}" alt="">`, title);
    } else if (kind === 'text' && text != null) {
      printHtml(`<pre>${escapeHtml(text)}</pre>`, title);
    } else if (kind === 'pdf' && pagesRef.current) {
      const imgs = Array.from(pagesRef.current.querySelectorAll('canvas'))
        .map((c) => `<img src="${c.toDataURL('image/png')}" alt="">`)
        .join('');
      if (imgs) printHtml(imgs, title);
    }
  };

  const canPrint =
    kind === 'image' || (kind === 'text' && text != null) || (kind === 'pdf' && !pdfLoading && !error);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex flex-col"
      onContextMenu={locked ? noContextMenu : undefined}
    >
      <header className="h-14 px-4 flex items-center gap-3 text-white shrink-0">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{fileName || 'Attachment'}</div>
          {locked && (
            <div className="text-xs text-amber-300">🔒 View & Print only — downloads disabled by the sender</div>
          )}
        </div>
        {canPrint && (
          <button onClick={print} className="px-3 py-1.5 rounded-full text-sm bg-white/15 hover:bg-white/25">
            🖨 Print
          </button>
        )}
        {!locked && (
          <button
            onClick={() => downloadFile(fileUrl, fileName)}
            className="px-3 py-1.5 rounded-full text-sm bg-white/15 hover:bg-white/25"
          >
            ⬇ Download
          </button>
        )}
        <button onClick={onClose} title="Close" className="w-9 h-9 rounded-full hover:bg-white/15 text-xl">
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-auto p-4 select-none" onClick={(e) => e.target === e.currentTarget && onClose()}>
        {error && <p className="text-center text-white/80 mt-20">{error}</p>}

        {!error && kind === 'image' && (
          <img
            src={fileUrl}
            alt={fileName || ''}
            draggable={false}
            onContextMenu={locked ? noContextMenu : undefined}
            className="mx-auto max-h-full max-w-full object-contain"
          />
        )}

        {!error && kind === 'video' && (
          <video
            src={fileUrl}
            controls
            autoPlay
            controlsList={locked ? 'nodownload noremoteplayback' : undefined}
            disablePictureInPicture={locked}
            onContextMenu={locked ? noContextMenu : undefined}
            className="mx-auto max-h-full max-w-full"
          />
        )}

        {!error && kind === 'text' && (
          <pre className="max-w-4xl mx-auto bg-white rounded-lg p-4 text-xs whitespace-pre-wrap break-words">
            {text ?? 'Loading…'}
          </pre>
        )}

        {!error && kind === 'pdf' && (
          <>
            {pdfLoading && <p className="text-center text-white/80 mt-20">Loading PDF…</p>}
            <div ref={pagesRef} className="max-w-4xl mx-auto" />
          </>
        )}

        {!error && kind === null && (
          <p className="text-center text-white/80 mt-20 max-w-md mx-auto">
            {locked
              ? "This file type can't be previewed here, and the sender has turned off downloads. Ask them to send it as a PDF."
              : "This file type can't be previewed here — use Download to open it."}
          </p>
        )}
      </div>
    </div>
  );
}
